import { CUSTOMER_PRIVACY_POLICY } from './customer-privacy'

/**
 * Legal document types and the versions rendered during signup.
 *
 * The database (`public.legal_documents`) is the runtime authority for which
 * published version is current. This constant exists so the web app can stamp
 * the version it rendered into signup metadata before a session exists. Draft
 * versions are deliberately distinct from release versions: draft metadata is
 * never accepted by the API and cannot suppress the eventual publication gate.
 *
 * Publishing a new version is a two-step lockstep change, same pattern as the
 * reauth purpose enum: (1) a new migration inserts the new `legal_documents`
 * row and flips `is_current`, (2) these versions and publication status are
 * bumped in the same PR.
 */
export const LEGAL_DOCUMENT_TYPES = [
  'terms_of_service',
  'privacy_policy',
] as const;

export type LegalDocumentType = (typeof LEGAL_DOCUMENT_TYPES)[number];

export type LegalPublicationStatus = 'draft' | 'published';

/**
 * Every document type the admin publication surface manages — the login-gate
 * set above PLUS role-scoped documents with their own acceptance flows.
 * Deliberately a SEPARATE constant: widening `LEGAL_DOCUMENT_TYPES` itself
 * would make the account login gate fail closed for every session the moment
 * a role-scoped document row exists (the 2026-07-21 provider-agreement
 * incident, fixed in PR #1215). Admin lifecycle surfaces import this; login
 * and signup gates must keep importing `LEGAL_DOCUMENT_TYPES`.
 */
export const MANAGED_LEGAL_DOCUMENT_TYPES = [
  'terms_of_service',
  'privacy_policy',
  'provider_supplier_agreement',
] as const;

export type ManagedLegalDocumentType =
  (typeof MANAGED_LEGAL_DOCUMENT_TYPES)[number];

/**
 * Row lifecycle in `public.legal_documents` (`publication_status` column).
 * Distinct from `LegalPublicationStatus`, which describes the docs-portal
 * BODY publication mode and has no 'retired' state.
 */
export type LegalDocumentLifecycleStatus = 'draft' | 'published' | 'retired';

// The 2026-09-24 release updated privacy to v1.1 and Terms / provider supplier
// agreement to v1.2 for direct customer collection, data handling and the
// provider-neutral verification flow. The 2026-09-25 privacy v1.2 revision
// adds partner verification and payout recipients. The 2026-09-28 privacy
// v1.3 revision clarifies Stripe Identity processing and rights. The 2026-10-28
// release (founder request) updates privacy to v1.4 (Decor, finished-print
// photos, preview copies, Doodles sign-in codes, saved addresses and
// self-service deletion, Invisible Turnstile) and Terms to v1.3 (shipped and
// automatic delivery, uploaded print photos), published by migration
// 20261028170000_privacy_notice_1_4_terms_1_3.sql. New acceptance is required;
// old evidence remains attached to its historical version. Keep the migration
// in lockstep.
// v1.0 was founder-reviewed and published by the 2026-07-10 decision on
// PR #989. This history does not imply independent legal-counsel approval.
// Published by migration
// 20260711100000_publish_legal_documents_v1.sql (lockstep).
export const LEGAL_PUBLICATION_STATUS = 'published' as LegalPublicationStatus;

export const CURRENT_LEGAL_VERSIONS: Readonly<
  Record<LegalDocumentType, string>
> = {
  terms_of_service: '1.3',
  privacy_policy: CUSTOMER_PRIVACY_POLICY.noticeVersion,
};

/** Body revision dates only. The published effective time is the current database row's effective_at, set at publication. */
export const CURRENT_LEGAL_REVISED_AT: Readonly<
  Record<ManagedLegalDocumentType, string>
> = {
  terms_of_service: '2026-10-28T00:00:00.000Z',
  privacy_policy: '2026-10-28T00:00:00.000Z',
  provider_supplier_agreement: '2026-09-24T00:00:00.000Z',
};

/**
 * Docs-portal body version for the `provider_supplier_agreement`.
 *
 * This is deliberately SEPARATE from `LEGAL_DOCUMENT_TYPES` /
 * `CURRENT_LEGAL_VERSIONS`, which drive the account-level login legal gate
 * (Terms of Service + Privacy Policy only). The provider supplier agreement
 * is a provider-onboarding document, not an account-login gate — widening the
 * type/version manifest would incorrectly force every account to re-accept it
 * at sign-in.
 *
 * The onboarding wizard imports this constant to confirm the version the API
 * reports as the published provider agreement matches the version whose body
 * the `/docs` portal actually renders. When they diverge the wizard fails
 * closed (no accept flow) so a provider is never asked to accept a document
 * they cannot yet read. Bump it in lockstep with the rendered docs body
 * (`apps/web/src/docs/sections/ProviderSupplierAgreement.content.ts`) and any
 * published `legal_documents` row for the provider supplier agreement.
 */
export const PROVIDER_SUPPLIER_AGREEMENT_DOC_VERSION = '1.2';

/**
 * Publication mode of the provider supplier agreement's rendered docs body —
 * the SINGLE source of truth read by BOTH the docs portal (draft banner vs
 * governing note) and the onboarding wizard's acceptance gate (Codex review on
 * #1235: version equality alone let a published metadata row expose acceptance
 * while the rendered body still said "DRAFT - NOT EFFECTIVE ... cannot be
 * accepted"). While 'draft', the wizard never offers consent, regardless of
 * what is published in `legal_documents`. Go-live is one change: flip this to
 * 'published' in the same PR that publishes the DB row. Deliberately separate
 * from LEGAL_PUBLICATION_STATUS — the ToS/Privacy sign-off timeline is
 * independent of the supplier agreement's.
 *
 * v1.0 published per the founder decision of 2026-09-02. Publication records
 * founder review and does not imply independent legal-counsel approval.
 * Published by migration 20260902110000_publish_provider_supplier_agreement_v1.sql
 * (lockstep). Until then every provider was payout-ineligible
 * (`PROVIDER_AGREEMENT_NOT_ACCEPTED`) because no current published row existed.
 */
export const PROVIDER_SUPPLIER_AGREEMENT_PUBLICATION_STATUS =
  'published' as LegalPublicationStatus;

/**
 * Shape stamped into Supabase `auth.signUp` user metadata when the signup
 * form's required consent checkbox is ticked. Read back on first login to
 * record the acceptance through the API (which adds IP/user-agent audit
 * context) without re-prompting the user.
 */
export interface SignupLegalConsentMetadata {
  terms_of_service: string;
  privacy_policy: string;
  consented_at: string;
}
